/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SystemReportSummaryModule
 */

import {
    Component,
    Input,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import * as globalL10n from 'global-l10n';

import './system-report-summary-card.component.less';
import * as l10n from './system-report-summary-card.l10n';
import { PROGRESS_CLASSES } from '../../constants/system-reports-page.constant';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Interface for representing summary of a system report.
 */
export interface ISystemReportSummary {
    /**
     * Number of errors in the system report.
     */
    errors: number;
    /**
     * Number of success in the system report.
     */
    success: number;
    /**
     * Number of warnings in the system report.
     */
    warnings: number;
    /**
     * Total time taken to run the system report in milliseconds.
     */
    totalTime: number;
    /**
     * Represents the maximum value for the progress bar.
     */
    maxProgressBarValue: number;
}

/**
 * Interface for representing start and end time of a system report.
 */
export interface ISystemReportStartAndEndTime {
    startTime: string;
    endTime: string;
}

/**
 * @description Component for System Report Summary Card.
 * @author Deepak Chaudhary
 */
@Component({
    selector: 'system-report-summary-card',
    templateUrl: './system-report-summary-card.component.html',
})
export class SystemReportSummaryCardComponent implements OnChanges {
    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public readonly progressClass = PROGRESS_CLASSES;

    public totalProcessTime = 0;

    @Input()
    public systemReportName: string;

    @Input()
    public systemReportStartAndEndTime: ISystemReportStartAndEndTime;

    @Input()
    public isLoading: boolean;

    @Input()
    public systemReadinessSummaryDetails: ISystemReportSummary;

    @Input()
    public dryRunSummaryDetails: ISystemReportSummary;

    @Input()
    public systemTaskDetails: ISystemReportSummary;

    constructor(l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const {
            systemReadinessSummaryDetails: { currentValue: { totalTime: readinessTotalTime = 0 } = {} } = {},
            dryRunSummaryDetails: { currentValue: { totalTime: dryRunTotalTime = 0 } = {} } = {},
            systemTaskDetails: { currentValue: { totalTime: systemTaskTotalTime = 0 } = {} } = {},
        } = changes;

        this.totalProcessTime = readinessTotalTime + dryRunTotalTime + systemTaskTotalTime;
    }
}
